import React, { useState, useEffect, useMemo } from "react";
import "./DoctorData.css";
import Avatar from "../../assets/avatar.png";
import { ToggleButton, ToggleButtonGroup, Spinner } from "react-bootstrap";
import axios from "axios";
import { Modal } from "react-bootstrap";
import ConfirmModal from "../../components/modal/Modal";
import { Link } from "react-router-dom";
import { FaRegEye, FaRegPenToSquare } from "react-icons/fa6";
import { FaUserNurse, FaUserMd, FaHospitalUser, FaClinicMedical, FaNotesMedical, FaFileExport, FaStethoscope } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ToastContainer, toast } from "react-toastify";
import { apiUrl, CONSULTATION_REGISTRATION, REGISTRATION_SOURCES } from "../../config/appConfig";

/**
 * Tab label to the role_id the API filters on. Hoisted out of the component because the
 * same mapping is needed by the initial fetch, the delete refresh and the photo-edit refresh.
 */
const ROLE_ID_BY_TAB = {
  "Doctor Online Consultation": 4,
  Pharmacy: 5,
  "Doctor Home Consultation": 6,
  Nurse: 7,
  "Physiotherapy / Imaging / Lab": 8,
};

const DEFAULT_ROLE_ID = 4;

/**
 * Mirrors the allow-list and ceiling the professional service enforces in
 * S3Service.validateImageUpload. Kept in step so a file the browser accepts is not then
 * rejected by the backend after the user has waited for the upload.
 */
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB

const CustomModal = ({ showModal, onClose, details, initialImageUrl }) => {
  const [Imgurl, setImgUrl] = useState(initialImageUrl || Avatar);
  const capitalize = (str) => str?.charAt(0)?.toUpperCase() + str?.slice(1);

  useEffect(() => {
    // If the table already resolved this professional's image, show it
    // instantly instead of re-fetching a presigned URL.
    if (initialImageUrl) {
      setImgUrl(initialImageUrl);
      return;
    }

    const fetchUrl = async () => {
      if (!details || !details?.profile_picture_url) {
        setImgUrl(Avatar);
        return;
      }
      try {
        const res = await axios.post(
          apiUrl('/professionals/view-url'),
          { key: details?.profile_picture_url, content_type: "image/jpeg" },
          { headers: { accept: "application/json", "Content-Type": "application/json" } }
        );
        setImgUrl(res.data.data.view_url || Avatar);
      } catch (err) {
        console.error("Error fetching presigned URL:", err);
        setImgUrl(Avatar);
      }
    };
    fetchUrl();
  }, [details, initialImageUrl]);

  if (!details) return null;

  return (
    <Modal
      className="viewModal"
      show={showModal}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton />

      <Modal.Body className="text-center">
        <div className="viewModal-content">
          <div className="profileImg">
            <img
              src={Imgurl}
              alt="Profile"
              className="img-fluid"
              loading="eager"
              decoding="async"
              onError={(e) => {
                e.target.src = Avatar;
              }}
            />
          </div>
          <h5 className="modal-title">{capitalize(details?.full_name) || "Details"}</h5>
          <div className="text-start mt-4 detailsWrapper">
            <div className="row mb-2">
              <div className="col-6">
                <strong>Phone:</strong>
              </div>
              <div className="col-6">{details?.contact_phone || "N/A"}</div>
            </div>
            <div className="row mb-2">
              <div className="col-6">
                <strong>Bio:</strong>
              </div>
              <div className="col-6">{capitalize(details?.short_bio) || "N/A"}</div>

            </div>

            {details?.role_id === 8 && (
              <>
                {details?.center && (
                  <div className="row mb-2">
                    <div className="col-6">
                      <strong>Center Name:</strong>
                    </div>
                    <div className="col-6">{capitalize(details.center)}</div>
                  </div>
                )}
                {details?.center_type && (
                  <div className="row mb-2">
                    <div className="col-6">
                      <strong>Center Type:</strong>
                    </div>
                    <div className="col-6">{details.center_type}</div>
                  </div>
                )}
                {details?.street && (
                  <div className="row mb-2">
                    <div className="col-6">
                      <strong>Street:</strong>
                    </div>
                    <div className="col-6">{capitalize(details.street)}</div>
                  </div>
                )}
                {details?.area && (
                  <div className="row mb-2">
                    <div className="col-6">
                      <strong>Area:</strong>
                    </div>
                    <div className="col-6">{capitalize(details.area)}</div>
                  </div>
                )}
                {details?.center_address && (
                  <div className="row mb-2">
                    <div className="col-6">
                      <strong>Center Address:</strong>
                    </div>
                    <div className="col-6">{details.center_address}</div>
                  </div>
                )}
              </>
            )}

            {details?.role_id !== 8 && Array.isArray(details?.hospital_details) && details?.hospital_details?.length > 0 && (
              <div className="row mb-2">
                <div className="col-12 mb-1 px-0">
                  <strong>Hospital Details:</strong>
                </div>
                <div className="col-12 px-0 pb-1">
                  <div className="row mb-1 bg-light py-1">
                    <div className="col-6 fw-semibold">Names</div>
                    <div className="col-6 fw-semibold">Location</div>
                  </div>
                  {details?.hospital_details?.map((hospital, index) => (
                    <div className={`row mb-1 ${index === 0 ? "" : "border-top pt-1"}`} key={index}>
                      <div className="col-6">{capitalize(hospital.name)} -</div>
                      <div className="col-6"> {hospital?.address}
                        {index < details?.hospital_details?.length - 1 ? ", " : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {details?.pharmacy_name && (
              <div className="row mb-2">
                <div className="col-6">
                  <strong>Pharmacy:</strong>
                </div>
                <div className="col-6">{capitalize(details?.pharmacy_name)}</div>
              </div>
            )}
            {details?.specialty && (
              <div className="row mb-2">
                <div className="col-6">
                  <strong>Specialty:</strong>
                </div>
                <div className="col-6">{capitalize(details?.specialty)}</div>
              </div>
            )}
            {details?.location && (
              <div className="row mb-2">
                <div className="col-6">
                  <strong>Location:</strong>
                </div>
                <div className="col-6">{capitalize(details?.location)}</div>
              </div>
            )}
            {details?.longitude && (
              <div className="row mb-2">
                <div className="col-6">
                  <strong>longitude(X):</strong>
                </div>
                <div className="col-6">{capitalize(details?.longitude)}</div>
              </div>
            )}
            {details?.latitude && (
              <div className="row mb-2">
                <div className="col-6">
                  <strong>latitude(Y):</strong>
                </div>
                <div className="col-6">{capitalize(details?.latitude)}</div>
              </div>
            )}
            {details?.years_of_experience != null && (
              <div className="row mb-2">
                <div className="col-6">
                  <strong>Experience:</strong>
                </div>
                <div className="col-6">{details?.years_of_experience} yrs</div>
              </div>
            )}
            <div className="row mb-2">
              <div className="col-6">
                <strong>Interested in Online:</strong>
              </div>
              <div className="col-6">
                {details?.online_consultation ? "Yes" : "No"}
              </div>
            </div>
            <div className="row mb-2">
              <div className="col-6">
                <strong>Region:</strong>
              </div>
              <div className="col-6">{capitalize(details?.region) || "Baghdad"}</div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

/**
 * Photo-only edit dialog.
 *
 * Deliberately narrow: it changes the profile picture and nothing else, so an admin correcting
 * an avatar cannot accidentally alter a professional's details. Uploading and persisting happen
 * in a single PATCH, and the service deletes the object the record previously pointed at.
 */
const EditPhotoModal = ({ show, professional, currentImageUrl, onClose, onSaved }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Revoke the blob URL whenever the selection changes or the dialog unmounts, otherwise
  // picking several files in a row leaks one object URL per attempt.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Start clean each time the dialog opens, including for a different professional.
  useEffect(() => {
    if (show) {
      setFile(null);
      setError("");
      setSaving(false);
    }
  }, [show, professional?.id]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) {
      setFile(null);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(selected.type)) {
      setError("Invalid file type. Only JPG, JPEG, PNG, GIF or WebP are allowed.");
      setFile(null);
      return;
    }
    if (selected.size > MAX_IMAGE_BYTES) {
      setError("File is too large. Max size is 10MB.");
      setFile(null);
      return;
    }
    setError("");
    setFile(selected);
  };

  const handleSave = async () => {
    if (!file || !professional?.id) return;
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.patch(
        apiUrl(`/professionals/${professional.id}/photo`),
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Profile photo updated successfully");
        onSaved({
          previousKey: professional.profile_picture_url,
          newKey: res.data?.data?.key || null,
          newUrl: res.data?.data?.url || null,
        });
      } else {
        setError(res.data?.message || "Failed to update profile photo");
      }
    } catch (err) {
      console.error("Error updating profile photo:", err);
      setError(err.response?.data?.message || "Failed to update profile photo");
    } finally {
      setSaving(false);
    }
  };

  if (!professional) return null;

  const displayName = professional.center || professional.full_name || "this professional";

  return (
    <Modal
      className="viewModal"
      show={show}
      onHide={saving ? undefined : onClose}
      backdrop="static"
      keyboard={!saving}
      centered
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Edit Profile Photo</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted text-center mb-3" style={{ fontSize: "0.9rem" }}>
          Updating the photo for <strong>{displayName}</strong>. No other details will change.
        </p>

        <div className="d-flex justify-content-center align-items-center gap-4 mb-4">
          <div className="text-center">
            <div className="mb-2 text-muted" style={{ fontSize: "0.8rem" }}>Current</div>
            <img
              src={currentImageUrl || Avatar}
              alt="Current profile"
              height={96}
              width={96}
              style={{ objectFit: "cover", borderRadius: "50%", border: "1px solid #dee2e6" }}
              onError={(e) => {
                e.target.src = Avatar;
              }}
            />
          </div>

          <div className="text-center">
            <div className="mb-2 text-muted" style={{ fontSize: "0.8rem" }}>New</div>
            <img
              src={previewUrl || Avatar}
              alt="New profile preview"
              height={96}
              width={96}
              style={{
                objectFit: "cover",
                borderRadius: "50%",
                border: "1px solid #dee2e6",
                opacity: previewUrl ? 1 : 0.4,
              }}
            />
          </div>
        </div>

        <label htmlFor="editProfilePhotoInput" className="form-label" style={{ fontSize: "0.9rem" }}>
          Choose a new photo
        </label>
        <input
          id="editProfilePhotoInput"
          type="file"
          className="form-control"
          accept={ALLOWED_IMAGE_TYPES.join(",")}
          onChange={handleFileChange}
          disabled={saving}
        />
        <div className="form-text">JPG, JPEG, PNG, GIF or WebP. Maximum 10MB.</div>

        {error && (
          <div className="alert alert-danger mt-3 mb-0 py-2" role="alert" style={{ fontSize: "0.85rem" }}>
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button className="btn nav-btnn" onClick={handleSave} disabled={!file || saving}>
          {saving ? (
            <span className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" /> Uploading...
            </span>
          ) : (
            "Save Photo"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Lists only registrations stored with `registrationSource`.
 * - Default MAIN_PLATFORM (/admin-registered): the main registry, without the consultation link's rows.
 * - DEDICATED_CONSULTATION_LINK (/admin-consultation-registered): only the Doctor Online / Home
 *   Consultation tabs (the roles that link accepts), and view + delete only.
 * `readOnly` (/admin-view-consultation-registered): the same list without the delete action.
 */
const DoctorData = ({ registrationSource = REGISTRATION_SOURCES.MAIN_PLATFORM, readOnly = false }) => {
  const isConsultationLinkView = registrationSource === CONSULTATION_REGISTRATION.SOURCE;
  const [page, setPage] = useState(1);
  const [tabActive, setTabActive] = useState("Doctor Online Consultation");
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imageUrls, setImageUrls] = useState({}); // Cache for image URLs: { key: url }
  const [loadingImages, setLoadingImages] = useState(new Set()); // Track which images are loading
  const [showModal, setShowModal] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null); // store API details
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [photoEditTarget, setPhotoEditTarget] = useState(null);
  const itemsPerPage = 10;

  const fetchPresignedUrl = async (key) => {
    if (!key) return null;
    try {
      const res = await axios.post(
        apiUrl('/professionals/view-url'),
        { key, content_type: "image/jpeg" },
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      return res.data.data.view_url || null;
    } catch (err) {
      console.error("Error fetching presigned URL:", err);
      return null;
    }
  };

  const fetchData = async (roleId) => {
    setLoading(true);
    try {
      const res = await axios.get(
        apiUrl('/professionals'),
        {
          params: { page, limit: itemsPerPage, role_id: roleId, registration_source: registrationSource },
          headers: { accept: "application/json" },
        }
      );

      const list = res.data?.data?.data || [];
      const totalCount = res.data?.data?.total || 0;

      // Show data immediately without waiting for images
      setData(list);
      setTotal(totalCount);
      setLoading(false);

      // Load images asynchronously in the background
      // Use functional update to get current imageUrls state
      setImageUrls((prevUrls) => {
        // Batch fetch all presigned URLs in parallel
        const imagesToLoad = list
          .filter((doc) => doc.profile_picture_url && !prevUrls[doc.profile_picture_url])
          .map((doc) => doc.profile_picture_url);

        // Mark images as loading
        if (imagesToLoad.length > 0) {
          setLoadingImages((prevLoading) => {
            const newSet = new Set(prevLoading);
            imagesToLoad.forEach((key) => newSet.add(key));
            return newSet;
          });
        }

        // Start fetching URLs for images that aren't cached
        const imageUrlPromises = list
          .filter((doc) => doc.profile_picture_url)
          .map(async (doc) => {
            const key = doc.profile_picture_url;
            // Skip if already cached
            if (prevUrls[key]) {
              return { key, url: prevUrls[key] };
            }
            const url = await fetchPresignedUrl(key);
            return { key, url };
          });

        // Update images as they load - use functional update to avoid stale closure
        Promise.allSettled(imageUrlPromises).then((results) => {
          setImageUrls((currentUrls) => {
            const newImageUrls = { ...currentUrls };
            results.forEach((result) => {
              if (result.status === "fulfilled" && result.value?.url) {
                newImageUrls[result.value.key] = result.value.url;
              }
            });
            return newImageUrls;
          });

          // Remove loaded images from loading set
          setLoadingImages((prev) => {
            const newSet = new Set(prev);
            results.forEach((result) => {
              if (result.status === "fulfilled" && result.value?.key) {
                newSet.delete(result.value.key);
              }
            });
            return newSet;
          });
        });

        // Return unchanged URLs (we'll update them in the Promise handler)
        return prevUrls;
      });
    } catch (err) {
      console.error("Error fetching data:", err);
      setData([]);
      setTotal(0);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(ROLE_ID_BY_TAB[tabActive] || DEFAULT_ROLE_ID);
  }, [tabActive, page]);

  const handleToggleChange = (val) => {
    // Clear data immediately to prevent flickering
    setData([]);
    setTotal(0);
    setImageUrls({}); // Clear image cache when switching tabs
    setLoadingImages(new Set()); // Clear loading images when switching tabs
    setTabActive(val);
    setPage(1);
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const showHospitalColumns = ["Doctor Online Consultation", "Doctor Home Consultation", "Nurse"].includes(tabActive);
  const showSpecialtyColumn = ["Doctor Online Consultation", "Doctor Home Consultation"].includes(tabActive);
  const showExperienceColumn = showSpecialtyColumn;

  const tableRows = useMemo(() => {
    if (!Array.isArray(data)) return [];

    // Return one row per professional, with all hospital details in the same row
    return data.map((doc) => ({ doc }));
  }, [data]);

  let columnCount = 9; // #, Name, Type, Location, Region, Phone, Bio, Registered, Action
  if (showSpecialtyColumn) columnCount += 1; // specialty
  if (showExperienceColumn) columnCount += 1; // experience
  if (tabActive === "Physiotherapy / Imaging / Lab") columnCount += 2; // street and area

  // Fetch full details when View is clicked
  const handleViewClick = async (id) => {
    try {
      const res = await axios.get(
        apiUrl(`/professionals/${id}`),
        {
          headers: { accept: "application/json" },
        }
      );
      setSelectedDetail(res.data?.data || null);
      setShowModal(true);
    } catch (err) {
      console.error("Error fetching detail:", err);
    }
  };

  const handleDeleteClick = async (id) => {
    try {
      const res = await axios.delete(
        apiUrl(`/professionals/${id}`),
        {
          headers: { accept: "application/json" },
        }
      );

      if (res.data.success) {
        toast.success("Data deleted successfully");

        // After deletion, refetch data for the current tab
        const roleId = ROLE_ID_BY_TAB[tabActive] || DEFAULT_ROLE_ID;

        // If last item on page is deleted and page > 1, go to previous page
        const remainingItems = data.length - 1;
        if (remainingItems === 0 && page > 1) {
          setPage(page - 1);
        } else {
          fetchData(roleId);
        }
      } else {
        toast.error("Failed to delete");
      }
    } catch (err) {
      console.error("Error deleting data:", err);
      toast.error("Error deleting data");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDetail(null);
  };

  const requestDelete = (id) => {
    setPendingDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const requestPhotoEdit = (doc) => {
    setPhotoEditTarget(doc);
  };

  const closePhotoEdit = () => {
    setPhotoEditTarget(null);
  };

  /**
   * Swap the cached presigned URL so the table shows the replacement immediately.
   *
   * The old key is dropped because the service has deleted that object, which would otherwise
   * leave a presigned URL in the cache pointing at something that no longer exists. The refetch
   * that follows brings the row's `profile_picture_url` up to date.
   */
  const handlePhotoSaved = ({ previousKey, newKey, newUrl }) => {
    setImageUrls((prev) => {
      const next = { ...prev };
      if (previousKey) delete next[previousKey];
      if (newKey && newUrl) next[newKey] = newUrl;
      return next;
    });
    closePhotoEdit();
    fetchData(ROLE_ID_BY_TAB[tabActive] || DEFAULT_ROLE_ID);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    await handleDeleteClick(pendingDeleteId);
    setShowDeleteConfirm(false);
    setPendingDeleteId(null);
  };

  const capitalize = (str) => {
    if (str === null || str === undefined) return "";
    if (typeof str !== "string") {
      if (str === null || str === undefined) return "";
      return String(str);
    }
    return str?.charAt(0)?.toUpperCase() + str?.slice(1);
  };

  // Safe truncate function
  const truncate = (str, length = 18) => {
    if (str === null || str === undefined) return "";
    if (typeof str !== "string") {
      if (str === null || str === undefined) return "";
      str = String(str);
    }
    return str?.length > length ? str.slice(0, length) + " ..." : str;
  };

  // Format the exact registration date and time
  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Export pharmacy data to CSV via API
  const handleExportPharmacyData = async () => {
    if (tabActive !== "Pharmacy") return;
    
    try {
      toast.info("Exporting pharmacy data...");
      
      // Call the API endpoint to get CSV file
      // Note: REACT_APP_BASE_URL already includes /api/v1
      const response = await axios.get(
        apiUrl('/professionals/export/pharmacy'),
        {
          responseType: 'blob', // Important: receive as blob for file download
          headers: { accept: "text/csv" },
        }
      );

      // Read the blob as text to add UTF-8 BOM for proper Excel encoding
      const text = await response.data.text();
      
      // Add UTF-8 BOM (Byte Order Mark) to help Excel recognize UTF-8 encoding
      // This is especially important for Arabic and other non-ASCII characters
      const BOM = '\uFEFF';
      const csvWithBOM = BOM + text;
      
      // Create blob with UTF-8 BOM
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `pharmacy_export_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      URL.revokeObjectURL(url);
      
      toast.success("Pharmacy data exported successfully");
    } catch (error) {
      console.error("Error exporting pharmacy data:", error);
      toast.error(error.response?.data?.message || "Failed to export pharmacy data");
    }
  };

  return (
    <div className="apt-main-content" style={{ minHeight: "100vh" }}>
      <div className="innerWrapper">
        {isConsultationLinkView && (
          <header className="cr-page-header">
            <span className="cr-page-icon" aria-hidden="true">
              <FaStethoscope />
            </span>
            <div className="cr-page-text">
              <h1 className="cr-page-title">Consultation Doctor Registrations</h1>
              <p className="cr-page-subtitle">
                Doctors who registered for online and home consultations through the Tiryaq Professional link.
              </p>
            </div>
            <div className="cr-page-badges">
              {!loading && (
                <span className="cr-badge cr-badge-count">
                  {total} {total === 1 ? "doctor" : "doctors"}
                </span>
              )}
              {readOnly && (
                <span className="cr-badge cr-badge-mode">
                  <FaRegEye aria-hidden="true" /> View only
                </span>
              )}
            </div>
          </header>
        )}
        <div className="d-flex justify-content-between align-items-center mb-4 tblHdBlock">
          <div className="dataPageToggle">
            <ToggleButtonGroup
              type="radio"
              name="tableData"
              value={tabActive}
              onChange={handleToggleChange}
            >
              <ToggleButton
                id="doctor"
                value="Doctor Online Consultation"
                variant={
                  tabActive === "Doctor Online Consultation" ? "primary" : "outline-primary"
                }
                style={{ minWidth: 80 }}
              >
                <FaUserMd className="me-2" />
                Doctor Online Consultation
              </ToggleButton>
              {!isConsultationLinkView && (
              <ToggleButton
                id="pharmacy"
                value="Pharmacy"
                variant={
                  tabActive === "Pharmacy" ? "primary" : "outline-primary"
                }
                style={{ minWidth: 80 }}
              >
                <FaNotesMedical className="me-2" />
                Pharmacy
              </ToggleButton>
              )}
              <ToggleButton
                id="doctor-home"
                value="Doctor Home Consultation"
                variant={
                  tabActive === "Doctor Home Consultation" ? "primary" : "outline-primary"
                }
                style={{ minWidth: 80 }}
              >
                <FaClinicMedical className="me-2" />
                Doctor Home Consultation
              </ToggleButton>
              {!isConsultationLinkView && (
              <ToggleButton
                id="nurse"
                value="Nurse"
                variant={
                  tabActive === "Nurse" ? "primary" : "outline-primary"
                }
                style={{ minWidth: 80 }}
              >
                <FaUserNurse className="me-2" />
                Nurse
              </ToggleButton>
              )}
              {!isConsultationLinkView && (
            <ToggleButton
              id="physiotherapy"
              value="Physiotherapy / Imaging / Lab"
              variant={
                tabActive === "Physiotherapy / Imaging / Lab" ? "primary" : "outline-primary"
              }
              style={{ minWidth: 80 }}
            >
              <FaHospitalUser className="me-2" />
              Physiotherapy / Imaging / Lab
            </ToggleButton>
              )}
            </ToggleButtonGroup>
          </div>
          {isConsultationLinkView ? null : (
          <div className="d-flex align-items-center gap-2">
            <Link to="/doctor-form" title="Add Doctor">
              <button className="nav-btnn btn d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <FaUserMd />
              </button>
            </Link>
            <Link to="/pharmacy-form" title="Add Pharmacy">
              <button className="nav-btnn btn d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <FaNotesMedical />
              </button>
            </Link>
            <Link to="/doctor-home-consultation-form" title="Add Doctor Home">
              <button className="nav-btnn btn d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <FaClinicMedical />
              </button>
            </Link>
            <Link to="/nurse-form" title="Add Nurse">
              <button className="nav-btnn btn d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <FaUserNurse />
              </button>
            </Link>
            <Link to="/physiotherapy-form" title="Add Physiotherapy / Imaging / Lab Center">
              <button className="nav-btnn btn d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <FaHospitalUser />
              </button>
            </Link>
            <Link to="/specialties-management" title="Manage Specialties">
              <button className="nav-btnn btn d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <FaStethoscope />
              </button>
            </Link>
          </div>
          )}
        </div>

        <div className="apt-appointments-section">
          {tabActive === "Pharmacy" && (
            <div className="apt-section-header mb-3" style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="apt-export-btn d-flex align-items-center gap-2"
                onClick={handleExportPharmacyData}
                title="Export Pharmacy Data"
                style={{ fontSize: "14px", padding: "8px 16px" }}
              >
                <FaFileExport /> Export Pharmacy Data
              </button>
            </div>
          )}
          <div className="apt-appointments-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>{tabActive === "Physiotherapy / Imaging / Lab" ? "Center Name" : "Full Name"}</th>
                  <th>
                    {tabActive === "Physiotherapy / Imaging / Lab"
                      ? "Center Type"
                      : tabActive === "Nurse"
                        ? "Hospital"
                        : showHospitalColumns
                          ? "Hospital"
                          : "Pharmacy Name"}
                  </th>
                  {tabActive === "Physiotherapy / Imaging / Lab" && <th>Street</th>}
                  {tabActive === "Physiotherapy / Imaging / Lab" && <th>Area</th>}
                  <th>
                    {tabActive === "Physiotherapy / Imaging / Lab"
                      ? "Location"
                      : showHospitalColumns
                        ? "Location"
                        : "Location"}
                  </th>
                  <th>Region</th>
                  {showSpecialtyColumn && <th>Specialty</th>}
                  <th>Phone</th>
                  <th>Bio</th>
                  {showExperienceColumn && <th>Experience</th>}
                  <th>Registered</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columnCount} style={{ textAlign: "center", padding: "40px" }}>
                      <div className="d-flex justify-content-center align-items-center gap-2">
                        <Spinner animation="border" variant="primary" />
                        <span>Loading data...</span>
                      </div>
                    </td>
                  </tr>
                ) : Array.isArray(tableRows) && tableRows.length > 0 ? (
                  tableRows.map((row, idx) => {
                    const { doc } = row;
                    const rowKey = doc?.id || `${idx}`;
                    
                    // Get all hospital details for this professional
                    const hospitalDetails = Array.isArray(doc?.hospital_details) && doc.hospital_details.length
                      ? doc.hospital_details
                      : [];
                    
                    const locationDisplay = tabActive === "Physiotherapy / Imaging / Lab"
                      ? (doc?.center_address ? (
                          <span className="cell-ellipsis" title={doc.center_address}>
                            {doc.center_address}
                          </span>
                        ) : "N/A")
                      : showHospitalColumns
                        ? (hospitalDetails.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {hospitalDetails.map((h, i) => (
                                <div key={i} className="cell-ellipsis" title={h.address || "N/A"}>
                                  {h.address || "N/A"}
                                </div>
                              ))}
                            </div>
                          ) : "N/A")
                        : (doc?.location ? (
                            <span className="cell-ellipsis" title={capitalize(doc.location)}>
                              {capitalize(doc.location)}
                            </span>
                          ) : "N/A");

                    return (
                    <tr key={rowKey}>
                      <td>{(page - 1) * itemsPerPage + idx + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <img
                            src={
                              doc.profile_picture_url && imageUrls[doc.profile_picture_url]
                                ? imageUrls[doc.profile_picture_url]
                                : Avatar
                            }
                            alt="avatar"
                            height={32}
                            width={32}
                            style={{ objectFit: "cover", borderRadius: "50%" }}
                            onError={(e) => {
                              e.target.src = Avatar;
                            }}
                            onLoad={() => {
                              if (doc.profile_picture_url) {
                                setLoadingImages((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(doc.profile_picture_url);
                                  return newSet;
                                });
                              }
                            }}
                          />
                          <span>
                            {tabActive === "Physiotherapy / Imaging / Lab"
                              ? capitalize(doc?.center) || "N/A"
                              : capitalize(doc?.full_name) || "N/A"}
                          </span>
                        </div>
                      </td>
                      <td>
                        {tabActive === "Physiotherapy / Imaging / Lab"
                          ? (doc?.center_type ? (
                              <span className="cell-ellipsis" title={capitalize(doc.center_type)}>
                                {capitalize(doc.center_type)}
                              </span>
                            ) : "N/A")
                          : showHospitalColumns
                            ? (hospitalDetails.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {hospitalDetails.map((h, i) => (
                                    <div key={i} className="cell-ellipsis" title={capitalize(h.name)}>
                                      {capitalize(h.name)}
                                    </div>
                                  ))}
                                </div>
                              ) : "N/A")
                            : (doc?.pharmacy_name ? (
                                <span className="cell-ellipsis" title={capitalize(doc.pharmacy_name)}>
                                  {capitalize(doc.pharmacy_name)}
                                </span>
                              ) : "N/A")}
                      </td>
                      {tabActive === "Physiotherapy / Imaging / Lab" && (
                        <td>{capitalize(doc?.street) || "N/A"}</td>
                      )}
                      {tabActive === "Physiotherapy / Imaging / Lab" && (
                        <td>{capitalize(doc?.area) || "N/A"}</td>
                      )}
                      <td>{locationDisplay}</td>
                      <td>{capitalize(doc?.region) || "Baghdad"}</td>
                      {showSpecialtyColumn && (
                        <td>{capitalize(doc?.specialty) || "N/A"}</td>
                      )}
                      <td>{doc.contact_phone || "N/A"}</td>
                      <td style={{ maxWidth: 180, cursor: "pointer" }} title={doc?.short_bio || "N/A"}>
                        {truncate(doc?.short_bio) || "N/A"}
                      </td>
                      {showExperienceColumn && (
                        <td>
                          {doc.years_of_experience != null
                            ? doc.years_of_experience + " yrs"
                            : "N/A"}
                        </td>
                      )}
                      <td style={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(doc?.created_at)}
                      </td>
                      <td>
                        <div className="d-flex gap-3 align-items-center">

                          <span
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            title="View details"
                            onClick={() => handleViewClick(doc.id)}
                          >
                            <FaRegEye size={20} />
                          </span>
                          {!isConsultationLinkView && (
                          <span
                            className="text-secondary"
                            style={{ cursor: "pointer" }}
                            title="Edit profile photo"
                            onClick={() => requestPhotoEdit(doc)}
                          >
                            <FaRegPenToSquare size={18} />
                          </span>
                          )}
                          {!readOnly && (
                          <span
                            className="text-danger"
                            style={{ cursor: "pointer" }}
                            title="Delete record"
                            onClick={() => requestDelete(doc.id)}
                          >
                            <RiDeleteBin6Line size={20} />
                          </span>
                          )}
                        </div>

                      </td>
                    </tr>
                  )})
                ) : (
                  <tr>
                    <td colSpan={columnCount} style={{ textAlign: "center" }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="apt-pagination">
            <span className="apt-pagination-info">
              Showing {(page - 1) * itemsPerPage + 1}-
              {Math.min(page * itemsPerPage, total)} of {total}
            </span>
            <div className="apt-pagination-controls">
              <button
                className="apt-pagination-btn"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                &lt; Previous
              </button>
              <div className="apt-page-numbers">
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx + 1}
                    className={`apt-page-btn${page === idx + 1 ? " active" : ""
                      }`}
                    onClick={() => setPage(idx + 1)}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              <button
                className="apt-pagination-btn apt-next-btn"
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
              >
                Next &gt;
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for details */}
      {showModal && selectedDetail && (
        <CustomModal
          showModal={showModal}
          onClose={handleCloseModal}
          details={selectedDetail} // pass details to modal
          initialImageUrl={
            selectedDetail?.profile_picture_url
              ? imageUrls[selectedDetail.profile_picture_url]
              : ""
          }
        />
      )}
      {/* Photo-only edit modal */}
      <EditPhotoModal
        show={Boolean(photoEditTarget)}
        professional={photoEditTarget}
        currentImageUrl={
          photoEditTarget?.profile_picture_url
            ? imageUrls[photoEditTarget.profile_picture_url]
            : ""
        }
        onClose={closePhotoEdit}
        onSaved={handlePhotoSaved}
      />

      {/* Confirm Delete Modal with same animation/style as Thank You */}
      <ConfirmModal
        showModal={showDeleteConfirm}
        onClose={cancelDelete}
        title="Confirm Deletion"
        message="Are you sure you want to permanently delete this record?"
        iconType="warning"
        showActions
        onConfirm={confirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DoctorData;
